import { useEffect, useState } from 'react';
import { RefreshCw, ShieldCheck } from 'lucide-react';

export type CaptchaChallenge = {
  id: string;
  prompt: string;
};

type Props = {
  challenge: CaptchaChallenge | null;
  loading?: boolean;
  error?: string;
  onRefresh: () => void;
  onVerified: (payload: { captchaId: string; captchaAnswer: number } | null) => void;
};

/** Lab63-style reCAPTCHA look — math check only, no Google / third-party widgets */
export default function RecaptchaField({
  challenge,
  loading,
  error,
  onRefresh,
  onVerified,
}: Props) {
  const [answer, setAnswer] = useState('');
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    setAnswer('');
    setVerified(false);
    onVerified(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challenge?.id]);

  function verify() {
    if (!challenge) return;
    const n = Number(answer);
    if (!Number.isFinite(n)) return;
    setVerified(true);
    onVerified({ captchaId: challenge.id, captchaAnswer: n });
  }

  function refresh() {
    setAnswer('');
    setVerified(false);
    onVerified(null);
    onRefresh();
  }

  return (
    <div
      style={{
        borderRadius: 14,
        border: verified ? '1.5px solid #1B8A5A' : '1.5px solid #F0C8D8',
        background: verified ? 'linear-gradient(135deg, #F0FFF6, #FFFBF8)' : 'linear-gradient(135deg, #FFFBF8, #FFF0F5)',
        padding: 16,
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: 6,
              background: verified ? '#1B8A5A' : 'linear-gradient(135deg,#C2185B,#E91E63)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ShieldCheck size={13} color="#fff" />
          </span>
          <strong style={{ fontSize: 13, fontWeight: 800, color: '#1A0A10', letterSpacing: '0.02em' }}>reCAPTCHA</strong>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          aria-label="Refresh captcha"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            border: 'none',
            background: 'transparent',
            color: '#C2185B',
            fontSize: 12,
            fontWeight: 700,
            cursor: loading ? 'wait' : 'pointer',
            fontFamily: 'inherit',
            opacity: loading ? 0.6 : 1,
          }}
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {loading || !challenge ? (
        <p style={{ margin: 0, fontSize: 13, color: '#9B6E7E' }}>Loading verification…</p>
      ) : verified ? (
        <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#1B8A5A', display: 'flex', alignItems: 'center', gap: 6 }}>
          <ShieldCheck size={16} /> Verified
        </p>
      ) : (
        <>
          <label htmlFor="recaptcha-answer" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#6B3A52', marginBottom: 8 }}>
            {challenge.prompt}
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <input
              id="recaptcha-answer"
              inputMode="numeric"
              autoComplete="off"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="Answer"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  verify();
                }
              }}
              style={{
                flex: '1 1 120px',
                boxSizing: 'border-box',
                background: '#fff',
                border: '1.5px solid #F0C8D8',
                borderRadius: 10,
                padding: '10px 14px',
                fontSize: 14,
                color: '#1A0A10',
                outline: 'none',
                fontFamily: 'inherit',
              }}
            />
            <button
              type="button"
              onClick={verify}
              style={{
                padding: '10px 16px',
                borderRadius: 10,
                border: 'none',
                background: 'linear-gradient(135deg,#C2185B,#E91E63)',
                color: 'white',
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Verify
            </button>
          </div>
          <p style={{ margin: '8px 0 0', fontSize: 11, color: '#9B6E7E' }}>
            Lab63 math check — no Google or third-party captcha widgets.
          </p>
        </>
      )}

      {error && <p style={{ margin: '8px 0 0', fontSize: 13, color: '#C62828' }}>{error}</p>}
      {verified && (
        <button
          type="button"
          onClick={refresh}
          style={{ marginTop: 8, border: 'none', background: 'none', color: '#C2185B', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
        >
          New verification
        </button>
      )}
    </div>
  );
}
